/**
 * CYBORG-988 — pure decision logic for the DELTA open.
 *
 * The problem: the cache paints the stored window instantly, and then the open
 * path refetches the whole tail (~50 rows) anyway. The skeleton is saved, the
 * bandwidth and the relay query are not — which is the half that does not scale
 * at 10k users switching conversations all day.
 *
 * WHY NOT `cyborg:sync` / `SyncMeta.lastSyncedSeq`
 * ------------------------------------------------
 * `lastSyncedSeq` (meta.ts) is genuinely dead — written by `writeScopeMessages`,
 * read by nobody — but it is the WRONG cursor for a per-conversation open, and
 * `cyborg:sync` is the wrong endpoint:
 *
 *   - `seq` is a WORKSPACE-wide counter (`workspace_sequences`, re-seeded from the
 *     DB max on relay restart), not a per-conversation one. A scope's max seq is a
 *     position in the workspace's global stream, so it says nothing about that
 *     conversation's own tail.
 *   - `cyborg:sync {lastSeq}` answers with `getMessagesSince(workspace, seq >
 *     lastSeq)` — EVERY channel and DM the caller can see, 500 per page. Handing it
 *     a quiet conversation's cursor asks for the workspace's entire backlog since
 *     that conversation last spoke. Strictly worse than fetching 50 rows.
 *   - Per-conversation reads order by the composite `(createdAt, seq)`, not by seq
 *     (pg-sync.ts getMessages: "seq ... isn't chronologically monotonic ...
 *     createdAt is the wall-clock truth for display order").
 *
 * The primitive that IS right already exists and is already shipped: the `after`
 * cursor on `cyborg:fetch_messages` / `cyborg:fetch_dm_messages` (b1 load-newer).
 * It takes a MESSAGE ID, resolves it to its `(createdAt, seq)` pair, and returns
 * the rows strictly after it in ASC order plus `hasMoreNewer`. No relay change is
 * needed and no new endpoint is invented.
 *
 * SAFETY MODEL — when a delta may replace the tail read
 * -----------------------------------------------------
 * A tail read does two jobs: it brings NEW messages, and it re-reads the last 50
 * rows so any edit / delete / pin / reaction the client missed is repaired. A
 * delta only does the first. So the delta is allowed exactly when the second job
 * is provably unnecessary — when this client has held an unbroken, gap-free
 * authenticated socket since that scope's last SUCCESSFUL fetch, because then
 * every mutation arrived as a live broadcast and was applied to both the pane and
 * the cache.
 *
 * That is what the scope marks below track. A mark is stamped ONLY from a FULL
 * read's fetch-success writeback (Caveat #29 — never optimistically, never on a
 * miss, and never from a DELTA writeback: see markScopeSynced, CYBORG-992 — a
 * delta re-stamping the mark reset the backstop clock on every open, so the
 * 30-minute bound could only ever fire for a conversation nobody opened).
 * Every signal that the socket may have lost frames wipes ALL marks: a cache
 * wipe and ANY connection event (a `seq_gap` suspends them until a drain names the
 * scopes it hit, CYBORG-1205) — a socket REPLACEMENT counts even when
 * it never announced a drop (CYBORG-991: a 4003 silent re-auth and an endpoint
 * switch both go straight from one socket to `connected` on the next). With no
 * mark, `planOpen` returns `tail` and the open behaves EXACTLY as it does today.
 *
 * ponytail: the marks are per-session memory only — a reload starts cold and pays
 * one tail read per conversation, which is the conservative direction. Persisting
 * them would need a `lastSyncedAt` on SyncMeta plus a durable "the socket stayed
 * healthy" proof; add that only if reload-time fetches ever show up as the cost.
 */

import { cyboDmLedgerKey } from "./pages.js";
import { channelScope, dmScope } from "./scope.js";

/** A row shaped enough to be a `(createdAt, seq)` cursor. `Message` satisfies it. */
export interface CursorRow {
  id: string;
  createdAt: number;
  seq?: number;
}

/** How the open path should ask the server for this conversation. */
export type OpenFetchPlan =
  /** Exactly today's read: the around-window when a divider is frozen, else limit 50. */
  | { kind: "tail" }
  /** Only the rows after `after` (a message id → `(createdAt, seq)` cursor). */
  | { kind: "delta"; after: string };

const TAIL: OpenFetchPlan = { kind: "tail" };

/**
 * Backstop ceiling on a scope mark. The disconnect + seq_gap wipes are the real
 * mechanism; this bounds the one case they cannot see — a socket that looks open
 * but is a backgrounded zombie which quietly missed an edit broadcast without
 * ever tripping the workspace seq-gap detector.
 *
 * CYBORG-992: it bounds the age of the last FULL read, NOT of the last write.
 * The mark used to be re-stamped by every successful writeback, including a
 * delta's — so opening a conversation reset its own backstop to zero and the
 * ceiling could only ever fire for a conversation nobody had opened in 30
 * minutes, which is precisely the conversation that does not need it. A scope
 * opened every minute for an hour stayed in a wrong-but-successful delta state
 * for the whole session, and the open was what kept it there.
 */
export const SYNCED_MARK_MAX_AGE_MS = 30 * 60_000;

// ── Scope marks (session-lived) ─────────────────────────────────────────────

const syncedScopes = new Map<string, number>();
let marksSuspended = false;

// What the server said about OLDER history when the scope's window was written.
// Same lifecycle as the sync mark: dropped wherever the mark is.
const olderHistoryByScope = new Map<string, boolean>();

export function markScopeOlderHistory(scopeKey: string, hasOlder: boolean): void {
  olderHistoryByScope.set(scopeKey, hasOlder);
}

export function scopeOlderHistory(scopeKey: string): boolean | null {
  return olderHistoryByScope.get(scopeKey) ?? null;
}

/**
 * Stamp a scope as network-synced. Callers stamp ONLY on a fetch SUCCESS, and
 * ONLY for a FULL read (the tail / around-window read that re-reads the last ~50
 * rows and therefore repairs a missed edit, delete, pin or reaction).
 *
 * A DELTA writeback must NOT reach here: a delta brings only what is new, so it
 * proves nothing about the rows below it, and re-stamping on one is what made
 * SYNCED_MARK_MAX_AGE_MS unable to fire. The value is "when this scope last took
 * a full read", which is the clock the backstop needs. (`lastAccessedAt` in
 * meta.ts stays the eviction clock and is deliberately a different one — it is
 * touched by every write, which is exactly what a backstop must not be.)
 */
export function markScopeSynced(scopeKey: string, now: number): void {
  syncedScopes.set(scopeKey, now);
}

export function scopeSyncedAt(scopeKey: string): number | null {
  if (marksSuspended) return null; // null: the existing "no mark" answer planOpen reads as tail
  if (holesByWs.has(scopeKey.slice(0, scopeKey.indexOf(":")))) return null; // null: as above
  return syncedScopes.get(scopeKey) ?? null;
}

// CYBORG-1205: a seq hole no longer wipes every mark. Until a drain has read across it,
// the workspace's marks are suspended (tail reads); the drain then drops only the scopes
// of rows that fell in the hole. A benign hole (a row this viewer cannot see) keeps all.
const holesByWs = new Map<string, Array<{ from: number; to: number }>>();

/** Rows with `from < seq < to` never reached this socket. */
export function noteSeqHole(workspaceId: string, from: number, to: number): void {
  if (!(to > from + 1)) return;
  const holes = holesByWs.get(workspaceId) ?? [];
  holes.push({ from, to });
  holesByWs.set(workspaceId, holes);
}

/**
 * A drain read every row with `drainedFrom < seq <= drainedTo`. Each hole inside that
 * range is settled: the scope of a row found in it lost that row, so its mark goes (a
 * row with no scope drops every mark). Holes the drain did not cover stay pending.
 */
export function settleSeqHoles(
  workspaceId: string,
  drainedFrom: number,
  drainedTo: number,
  rows: ReadonlyArray<{ seq?: number; scopeKey: string | null }>,
): void {
  const holes = holesByWs.get(workspaceId);
  if (!holes) return;
  const pending: Array<{ from: number; to: number }> = [];
  for (const hole of holes) {
    if (hole.from < drainedFrom || hole.to > drainedTo) {
      pending.push(hole);
      continue;
    }
    for (const row of rows) {
      if (typeof row.seq !== "number" || row.seq <= hole.from || row.seq >= hole.to) continue;
      if (row.scopeKey === null) {
        invalidateSyncedScopes();
        return;
      }
      forgetScopeSynced(row.scopeKey);
    }
  }
  if (pending.length > 0) holesByWs.set(workspaceId, pending);
  else holesByWs.delete(workspaceId);
}

/** Drop one scope's mark (the channel was left / deleted). */
export function forgetScopeSynced(scopeKey: string): void {
  syncedScopes.delete(scopeKey);
  olderHistoryByScope.delete(scopeKey);
}

/**
 * Drop EVERY mark. Called on any loss of the "unbroken socket" premise — any
 * connection event (a socket replacement is one, even a silent one: CYBORG-991),
 * a broadcast seq gap, a cache wipe / user switch. Deliberately
 * blunt: after a drop only the ACTIVE conversation is healed by the reconnect
 * burst, so every other scope's cache may have missed both messages and edits.
 */
export function invalidateSyncedScopes(): void {
  syncedScopes.clear();
  olderHistoryByScope.clear();
  holesByWs.clear();
  marksSuspended = false;
}

// CYBORG-1210: a connection event suspends the marks instead of dropping them. The reconnect
// change feed resumes one workspace's marks minus the scopes it saw change, only if no newer
// connection event came in meanwhile (the epoch); on any doubt the caller invalidates.
let suspendEpoch = 0;

/** Suspend every mark; returns the epoch a later resume must name. */
export function suspendSyncedScopes(): number {
  marksSuspended = true;
  return ++suspendEpoch;
}

export function syncedScopesSuspended(): number | null {
  return marksSuspended ? suspendEpoch : null; // null: nothing suspended
}

/** Keep `workspaceId`'s marks except `changed`; every other workspace's marks go. */
export function resumeSyncedScopes(
  workspaceId: string,
  changed: Iterable<string>,
  epoch: number,
): boolean {
  if (!marksSuspended || epoch !== suspendEpoch) return false;
  const prefix = `${workspaceId}:`;
  for (const key of syncedScopes.keys()) {
    if (!key.startsWith(prefix)) forgetScopeSynced(key);
  }
  for (const key of changed) forgetScopeSynced(key);
  for (const ws of holesByWs.keys()) {
    if (ws !== workspaceId) holesByWs.delete(ws);
  }
  marksSuspended = false;
  return true;
}

/**
 * The scope marks a batch of changed rows makes stale: channel, DM and agent-DM ledger. The
 * ledger mark is the page's own viewer-keyed key (`viewer` = viewerCacheKeySegment), or it never
 * matches; signed out there is no ledger window to mark.
 */
export function scopesTouchedByChanges(
  workspaceId: string,
  myIds: ReadonlySet<string>,
  rows: ReadonlyArray<{ channelId?: string | null; fromId: string; toId?: string | null }>,
  viewer: string,
): Set<string> {
  const keys = new Set<string>();
  for (const row of rows) {
    if (row.channelId) {
      if (viewer) keys.add(channelScope(workspaceId, row.channelId, viewer));
      continue;
    }
    for (const peer of dmPeers(myIds, row.fromId, row.toId ?? "")) {
      if (!peer || !viewer) continue;
      keys.add(dmScope(workspaceId, peer, viewer));
      keys.add(cyboDmLedgerKey(workspaceId, peer, viewer));
    }
  }
  return keys;
}

// ── Decisions (pure) ────────────────────────────────────────────────────────

/**
 * The `after` anchor for a warm window: the SECOND-newest server row, not the
 * newest.
 *
 * The server cursor is `createdAt > ref.createdAt OR (createdAt = ref.createdAt
 * AND seq > ref.seq)`, so a sibling sharing the anchor's exact millisecond AND
 * seq is excluded. Anchoring one row back re-returns the newest cached row (free
 * — `addMessage`/`mergeServer` dedup by id) and with it any same-ms sibling of it
 * we never saw, which is precisely where a burst of same-millisecond agent rows
 * gets cut. Optimistic `local-` rows carry no server cursor and are skipped.
 *
 * Returns null when there aren't two server rows to anchor between — the caller
 * then reads the tail, which is cheap on a window that small anyway.
 */
export function deltaCursor(cached: readonly CursorRow[]): string | null {
  const rows = cached.filter((m) => !m.id.startsWith("local-"));
  if (rows.length < 2) return null;
  const sorted = [...rows].sort((a, b) => a.createdAt - b.createdAt || (a.seq ?? 0) - (b.seq ?? 0));
  return sorted[sorted.length - 2].id;
}

/**
 * Decide how to read a conversation that is being opened.
 *
 * `tail` on every doubt — no scope mark, an unread block (the CYBORG-767
 * around-window open owns that case and must keep its read context above the
 * divider), a mark past the backstop, a clock that stepped backwards, or too
 * small a window to anchor. Correctness beats the optimisation: a hole in
 * someone's history costs far more than the 50 rows saved.
 */
export function planOpen(opts: {
  /** The window just painted from L1 — the rows the delta will be spliced onto. */
  cached: readonly CursorRow[];
  /** `scopeSyncedAt(scopeKey)`, or null when the scope carries no mark. */
  syncedAt: number | null;
  /** This conversation has unreads → the around-window read owns the open. */
  hasUnread: boolean;
  now: number;
  maxAgeMs?: number;
}): OpenFetchPlan {
  if (opts.syncedAt === null) return TAIL;
  if (opts.hasUnread) return TAIL;
  const age = opts.now - opts.syncedAt;
  // A negative age is a clock step back — the one moment an age check is worth
  // less than nothing, so refuse rather than trust it.
  if (!(age >= 0 && age <= (opts.maxAgeMs ?? SYNCED_MARK_MAX_AGE_MS))) return TAIL;
  const after = deltaCursor(opts.cached);
  return after === null ? TAIL : { kind: "delta", after };
}

/**
 * The tail read's page size, and the window `readCache` reads back
 * (`getRecent(scope, 50)`). One constant because the two must agree: `deltaHasMore`
 * reads "the cached window is FULL" off it.
 */
export const CACHED_WINDOW = 50;

/**
 * Older-history flag for a delta open. The tail read gets `hasMore` from the server;
 * a delta never asks about older rows, and leaving the field alone would carry the
 * PREVIOUS conversation's value over (the state classes are singletons). A full
 * cached window means older rows almost certainly exist; a short one means the scope
 * genuinely holds fewer, because eviction drops a scope whole and never trims a
 * window. Erring TRUE is self-correcting — the load-more path re-reads `hasMore`
 * from the server on the first scroll-up — while erring false would hide history.
 */
export function deltaHasMore(
  cached: readonly CursorRow[] | null,
  knownOlder: boolean | null = null,
): boolean {
  // An open-at-unread writes a SHORT window (~25 context rows + the unread head) of a
  // long channel, so length alone hid its history (CYBORG-1194). The server's answer wins.
  if (knownOlder === true) return true;
  return (cached?.length ?? 0) >= CACHED_WINDOW;
}

// The ONE open read for channels, human DMs and agent DMs: an alert target, else the
// planned delta unless it reports a gap, else around the frozen cursor or the tail.
// No await before the first fetchPage call (CYBORG-755, bootstrap-fetch-ordering).
export async function fetchPlannedPage<T extends { hasMoreNewer?: boolean }>(
  plan: OpenFetchPlan,
  fetchPage: (opts: {
    after?: string;
    limit?: number;
    aroundId?: string;
    aroundTs?: number;
  }) => Promise<T>,
  open: { frozen?: number | null; aroundId?: string } = {},
): Promise<{ page: T; usedDelta: boolean }> {
  if (open.aroundId)
    return { page: await fetchPage({ aroundId: open.aroundId }), usedDelta: false };
  if (plan.kind === "delta") {
    // Wire contract (relay message-changes.ts): a resume delta is `after` ALONE, no limit.
    const delta = await fetchPage({ after: plan.after });
    if (!isDeltaGap(delta)) return { page: delta, usedDelta: true };
  }
  const page =
    open.frozen != null
      ? await fetchPage({ aroundTs: open.frozen })
      : await fetchPage({ limit: CACHED_WINDOW });
  return { page, usedDelta: false };
}

/**
 * GAP DETECTION. The relay's `after` branch asks for `limit + 1` rows and reports
 * `hasMoreNewer` when there were more — i.e. more than one page arrived while we
 * were away, so what came back is the OLDEST rows after the cursor and the true
 * tail is still missing. That is the "offline past the page limit" case: discard
 * the delta and read the tail exactly as today.
 *
 * (The other direction is safe by construction: an empty page means no row is
 * newer than the cursor, and the anchor id stays resolvable because message
 * deletion is a `deleted_at` tombstone — the server's `after` ref lookup does not
 * filter on it — so a soft-deleted anchor still positions the cursor correctly.)
 */
export function isDeltaGap(res: { hasMoreNewer?: boolean }): boolean {
  return res.hasMoreNewer === true;
}

// The other party of a DM row; both sides when neither id is provably mine.
function dmPeers(myIds: ReadonlySet<string>, fromId: string, toId: string): string[] {
  if (myIds.has(fromId)) return [toId];
  if (myIds.has(toId)) return [fromId];
  return [fromId, toId];
}
